<script setup>
import { computed, ref } from 'vue';
import { useForm } from 'vee-validate';
import { useStore } from 'vuex';
import api from '@/api';
import BaseCheckbox from '@/components/Base/BaseCheckbox.vue';
import BaseConfirmationCard from '@/components/Base/BaseConfirmationCard.vue';
import BaseInputField from '@/components/Base/BaseInputField.vue';
import BaseLoader from '@/components/Base/BaseLoader.vue';

const DETAILS_MAX_LENGTH = 255;

const props = defineProps({
  interviewId: {
    type: [Number, String],
    required: true,
  },
  interviewType: {
    type: String,
    required: false,
    default: 'PROXIFY',
  },
  closeCancelDialog: {
    type: Function,
    required: true,
  },
  onCancel: {
    type: Function,
    required: false,
    default() {
      return () => {};
    },
  },
});

const store = useStore();

const reasons = ref([]);
const areReasonsLoading = ref(true);
const isLoading = ref(false);
const selectedReasons = ref([]);
const details = ref({});

const { handleSubmit } = useForm();

const getCancellingReasons = async () => {
  try {
    const { data } = await api.applications.interviews.getCancellingReasons();
    reasons.value = Object.entries(data.reasons ?? {}).map(
      ([value, { title, requires_details }]) => ({
        value,
        title,
        requiresDetails: Boolean(requires_details),
      })
    );
  } finally {
    areReasonsLoading.value = false;
  }
};
getCancellingReasons();

const isReasonSelected = (value) => selectedReasons.value.includes(value);

const toggleReason = (value, isSelected) => {
  if (isSelected) {
    selectedReasons.value = [...selectedReasons.value, value];
    return;
  }
  selectedReasons.value = selectedReasons.value.filter(
    (reason) => reason !== value
  );
  // Drop the details so an unticked reason cannot submit leftover text.
  details.value = { ...details.value, [value]: '' };
};

const hasSelectedReasons = computed(() =>
  Boolean(selectedReasons.value.length)
);

const cancelInterview = async () => {
  isLoading.value = true;
  try {
    const cancel =
      props.interviewType === 'KARAT'
        ? api.applications.interviews.cancelKaratInterview
        : api.applications.interviews.cancelInterview;

    await cancel(props.interviewId, {
      reasons: selectedReasons.value.map((reason) => ({
        reason,
        details: details.value[reason]?.trim() || null,
      })),
    });
    props.onCancel();
    store.commit('ui/addSnackbarMessage', {
      title: 'Interview cancelled successfully.',
      type: 'success',
      displayDuration: 5000,
    });
    props.closeCancelDialog();
  } finally {
    isLoading.value = false;
  }
};

const onSubmit = handleSubmit(cancelInterview);
</script>

<template>
  <BaseConfirmationCard
    :on-confirm="onSubmit"
    :on-close="closeCancelDialog"
    :disabled="!hasSelectedReasons"
    :is-loading="isLoading"
    confirm-button-text="Yes, cancel interview"
    confirm-button-destructive
    cancel-button-text="No, keep it"
    heading-class="!gap-1 !pb-5"
    width="560px"
    footer-border
  >
    <template #title>Cancel interview?</template>
    <template #subtitle>
      <div class="text-body-sm font-normal text-proxify-gray-600">
        Select the reason that best describes why the interview is being
        cancelled.
      </div>
    </template>
    <BaseLoader
      v-if="areReasonsLoading"
      class="mx-auto my-4"
      indeterminate
      size="24px"
    />
    <div
      v-else
      class="flex flex-col gap-2"
    >
      <template
        v-for="reason in reasons"
        :key="reason.value"
      >
        <BaseCheckbox
          :name="`cancelling-reason-${reason.value}`"
          :model-value="isReasonSelected(reason.value)"
          :label="reason.title"
          :data-testid="`cancelling-reason-${reason.value}`"
          @update:model-value="(value) => toggleReason(reason.value, value)"
        />
        <BaseInputField
          v-if="reason.requiresDetails && isReasonSelected(reason.value)"
          v-model="details[reason.value]"
          :name="`cancelling-reason-details-${reason.value}`"
          :max-length="DETAILS_MAX_LENGTH"
          :data-testid="`cancelling-reason-details-${reason.value}`"
          type="textarea"
          rows="4"
          placeholder="Please specify"
          required
          autofocus
          validate-on-value-update
        />
      </template>
    </div>
  </BaseConfirmationCard>
</template>
